import { useState, useEffect, useRef } from "react";

// ─── Seed Data ───────────────────────────────────────────────────────────────
const SEED_DOCTORS = [
  {
    id: "1",
    name: "Dr. Marcus Webb",
    specialty: "Cardiology",
    clinic: "Heart & Soul Medical",
    city: "Atlanta", state: "GA", zip: "30301",
    phone: "(404) 555-0192",
    instagram: "drwebbatl",
    linkedin: "marcus-webb-md",
    insurance: ["Aetna", "Blue Cross Blue Shield", "Medicare"],
    telehealth: true,
    accepting: true,
    bio: "Board-certified cardiologist bringing heart health to the community. Known for keeping it real with patients.",
    photo: "MW",
    invited: true,
    cookoutRole: "Brings the ribs",
    rating: 5,
  },
  {
    id: "2",
    name: "Dr. Keisha Thompson",
    specialty: "Pediatrics",
    clinic: "Little Stars Clinic",
    city: "Houston", state: "TX", zip: "77001",
    phone: "(713) 555-0341",
    instagram: "drkthompson",
    linkedin: "keisha-thompson-md",
    insurance: ["Medicaid", "UnitedHealthcare", "CHIP"],
    telehealth: true,
    accepting: true,
    bio: "Pediatrician dedicated to nurturing healthy futures. The kids love her and so does the cookout crew.",
    photo: "KT",
    invited: true,
    cookoutRole: "Handles the kids' table",
    rating: 5,
  },
  {
    id: "3",
    name: "Dr. Darnell Rivers",
    specialty: "Orthopedics",
    clinic: "Move Better Sports Medicine",
    city: "Chicago", state: "IL", zip: "60601",
    phone: "(312) 555-0877",
    instagram: "drdarnellmd",
    linkedin: "darnell-rivers-md",
    insurance: ["Cigna", "Aetna", "Blue Cross Blue Shield"],
    telehealth: false,
    accepting: true,
    bio: "Sports medicine and orthopedics specialist. Will tape your ankle at the cookout no questions asked.",
    photo: "DR",
    invited: true,
    cookoutRole: "Plays in the spades tournament",
    rating: 5,
  },
];

const SPECIALTIES = [
  "Family Medicine","Internal Medicine","Cardiology","Orthopedics",
  "Pediatrics","Dermatology","Neurology","Oncology","Psychiatry",
  "Gynecology","Ophthalmology","ENT","Urology","Gastroenterology",
  "Endocrinology","Rheumatology","Pulmonology","Urgent Care",
  "Physical Therapy","Chiropractic","Sports Medicine","Infectious Disease",
];

const INSURANCE_OPTIONS = [
  "Medicare","Medicaid","Blue Cross Blue Shield","UnitedHealthcare",
  "Aetna","Cigna","Kaiser","Humana","Tricare","Self Pay","CHIP",
];

const COOKOUT_ROLES = [
  "Brings the ribs","On the grill","Brings the potato salad","Handles the kids' table",
  "Plays in the spades tournament","Keeps the cooler stocked","Brings the sweet tea",
  "On the music","Brings mac & cheese","Brings the deviled eggs",
  "Dominoes champion","First to arrive, last to leave",
];

const BUCKET_COLORS = { A: "#2B5C8A", B: "#1a1a1a", C: "#6B6B6B", D: "#8B0000" };

function getGrade(rating) {
  if (rating >= 4.5) return "A";
  if (rating >= 3.5) return "B";
  if (rating >= 2.5) return "C";
  return "D";
}

function initials(name) {
  return name.split(" ").filter(Boolean).map(w => w[0]).join("").slice(0,2).toUpperCase();
}

// ─── Storage Helpers ──────────────────────────────────────────────────────────
const STORAGE_KEY = "drcookout-doctors";

async function loadDoctors() {
  try {
    const res = await window.storage.get(STORAGE_KEY, false);
    if (res && res.value) return JSON.parse(res.value);
  } catch (_) {}
  return null;
}

async function saveDoctors(docs) {
  try {
    await window.storage.set(STORAGE_KEY, JSON.stringify(docs), false);
  } catch (_) {}
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const FlameIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.5 0.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/>
  </svg>
);

const GrillIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 2v2H5c-1.1 0-2 .9-2 2v1h18V6c0-1.1-.9-2-2-2h-1V2h-2v2H8V2H6zm-3 7v2c0 3.87 2.76 7.1 6.45 7.82L8 22h2l1.45-3.18C11.63 18.94 11.81 19 12 19s.37-.06.55-.18L14 22h2l-1.45-3.18C18.24 18.1 21 14.87 21 11V9H3z"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const MapPinIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>
);

const StarIcon = ({ filled }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

// ─── Components ───────────────────────────────────────────────────────────────

function Avatar({ name, size = 56 }) {
  const colors = ["#2B5C8A","#8B0000","#1a5c1a","#5c1a5c","#5c4a1a"];
  const idx = name.charCodeAt(0) % colors.length;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: colors[idx],
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Bebas Neue', cursive", fontSize: size * 0.36,
      color: "#fff", letterSpacing: 1, flexShrink: 0,
      border: "2px solid rgba(255,255,255,0.2)",
    }}>
      {initials(name)}
    </div>
  );
}

function GradeBadge({ rating }) {
  const grade = getGrade(rating);
  return (
    <div style={{
      background: BUCKET_COLORS[grade],
      color: "#fff", borderRadius: 2,
      fontFamily: "'Bebas Neue', cursive",
      fontSize: 18, lineHeight: 1,
      padding: "4px 8px", letterSpacing: 1,
    }}>
      {grade}
    </div>
  );
}

function Stars({ rating, onChange }) {
  return (
    <div style={{ display: "flex", gap: 2, cursor: onChange ? "pointer" : "default" }}>
      {[1,2,3,4,5].map(s => (
        <span key={s} style={{ color: s <= rating ? "#CC5500" : "#ccc" }}
          onClick={() => onChange && onChange(s)}>
          <StarIcon filled={s <= rating} />
        </span>
      ))}
    </div>
  );
}

function InsurancePill({ label }) {
  return (
    <span style={{
      background: "rgba(43,92,138,0.10)", color: "#2B5C8A",
      borderRadius: 2, fontSize: 10, padding: "2px 7px",
      fontFamily: "'DM Mono', monospace", fontWeight: 500,
      border: "1px solid rgba(43,92,138,0.2)",
      whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

function DoctorCard({ doc, onEdit, onUninvite }) {
  const [expanded, setExpanded] = useState(false);
  const grade = getGrade(doc.rating);
  return (
    <div style={{
      background: "#F7F5F0",
      borderRadius: 3,
      borderTop: `3px solid ${BUCKET_COLORS[grade]}`,
      boxShadow: "0 2px 12px rgba(10,10,10,0.08)",
      overflow: "hidden",
      transition: "box-shadow 0.2s",
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 24px rgba(10,10,10,0.14)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 12px rgba(10,10,10,0.08)"}
    >
      {/* Card Header */}
      <div style={{ padding: "16px 16px 12px", display: "flex", gap: 12, alignItems: "flex-start" }}>
        <Avatar name={doc.name} size={52} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 18, color: "#0A0A0A", lineHeight: 1.1 }}>
                {doc.name}
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#2B5C8A", marginTop: 2, fontWeight: 500 }}>
                {doc.specialty}
              </div>
            </div>
            <GradeBadge rating={doc.rating} />
          </div>

          <div style={{ display: "flex", gap: 4, alignItems: "center", marginTop: 4 }}>
            <Stars rating={doc.rating} />
          </div>

          {doc.cookoutRole && (
            <div style={{
              marginTop: 6, display: "inline-flex", alignItems: "center", gap: 5,
              background: "rgba(204,85,0,0.10)", borderRadius: 2,
              padding: "3px 8px", border: "1px solid rgba(204,85,0,0.25)",
            }}>
              <FlameIcon />
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#CC5500", fontWeight: 500 }}>
                {doc.cookoutRole}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Location + Phone */}
      <div style={{ padding: "0 16px 10px", display: "flex", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 4, alignItems: "center", color: "#555", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
          <MapPinIcon />{doc.city}, {doc.state}
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "center", color: "#555", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
          <PhoneIcon />{doc.phone}
        </div>
        {doc.telehealth && (
          <span style={{ fontSize: 10, background: "#e8f5e9", color: "#2e7d32", borderRadius: 2, padding: "2px 6px", fontFamily: "'DM Mono', monospace", border: "1px solid #c8e6c9" }}>
            📱 Telehealth
          </span>
        )}
        {doc.accepting && (
          <span style={{ fontSize: 10, background: "#e3f2fd", color: "#1565c0", borderRadius: 2, padding: "2px 6px", fontFamily: "'DM Mono', monospace", border: "1px solid #bbdefb" }}>
            ✓ Accepting
          </span>
        )}
      </div>

      {/* Insurance */}
      {doc.insurance?.length > 0 && (
        <div style={{ padding: "0 16px 10px", display: "flex", flexWrap: "wrap", gap: 4 }}>
          {doc.insurance.slice(0, 3).map(ins => <InsurancePill key={ins} label={ins} />)}
          {doc.insurance.length > 3 && <InsurancePill label={`+${doc.insurance.length - 3} more`} />}
        </div>
      )}

      {/* Expanded Bio */}
      {expanded && doc.bio && (
        <div style={{ padding: "0 16px 10px" }}>
          <p style={{ fontFamily: "'EB Garamond', serif", fontSize: 14, color: "#333", margin: 0, lineHeight: 1.6 }}>
            {doc.bio}
          </p>
        </div>
      )}

      {/* Social + Actions */}
      <div style={{
        padding: "10px 16px", borderTop: "1px solid rgba(26,26,26,0.10)",
        display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", background: "rgba(26,26,26,0.02)"
      }}>
        {doc.instagram && (
          <a href={`https://instagram.com/${doc.instagram}`} target="_blank" rel="noreferrer"
            style={{ display: "flex", gap: 4, alignItems: "center", color: "#C13584", fontSize: 11, fontFamily: "'DM Mono', monospace", textDecoration: "none", fontWeight: 500 }}>
            <InstagramIcon />@{doc.instagram}
          </a>
        )}
        {doc.linkedin && (
          <a href={`https://linkedin.com/in/${doc.linkedin}`} target="_blank" rel="noreferrer"
            style={{ display: "flex", gap: 4, alignItems: "center", color: "#0A66C2", fontSize: 11, fontFamily: "'DM Mono', monospace", textDecoration: "none", fontWeight: 500 }}>
            <LinkedInIcon />LinkedIn
          </a>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => setExpanded(!expanded)}
          style={{ background: "none", border: "1px solid rgba(43,92,138,0.3)", borderRadius: 2, color: "#2B5C8A", fontFamily: "'DM Mono', monospace", fontSize: 10, padding: "4px 10px", cursor: "pointer", fontWeight: 500 }}>
          {expanded ? "Less" : "Bio"}
        </button>
        <button onClick={() => onEdit(doc)}
          style={{ background: "none", border: "1px solid rgba(26,26,26,0.2)", borderRadius: 2, color: "#444", fontFamily: "'DM Mono', monospace", fontSize: 10, padding: "4px 10px", cursor: "pointer" }}>
          Edit
        </button>
        <button onClick={() => onUninvite(doc.id)}
          style={{ background: "rgba(139,0,0,0.08)", border: "1px solid rgba(139,0,0,0.2)", borderRadius: 2, color: "#8B0000", fontFamily: "'DM Mono', monospace", fontSize: 10, padding: "4px 10px", cursor: "pointer" }}>
          Uninvite
        </button>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(10,10,10,0.55)", backdropFilter: "blur(2px)",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      overflowY: "auto", padding: "20px 12px 40px",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "#F7F5F0", borderRadius: 3, width: "100%", maxWidth: 480,
        borderTop: "3px solid #2B5C8A",
        boxShadow: "0 20px 60px rgba(10,10,10,0.3)",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(26,26,26,0.13)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 20, color: "#0A0A0A" }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#666", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

function DoctorForm({ initial, onSave, onCancel }) {
  const blank = {
    name: "", specialty: "", clinic: "", city: "", state: "", zip: "",
    phone: "", instagram: "", linkedin: "", bio: "",
    insurance: [], telehealth: false, accepting: true,
    cookoutRole: "", rating: 5,
  };
  const [form, setForm] = useState(initial ? { ...blank, ...initial } : blank);
  const [insInput, setInsInput] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addIns = (ins) => {
    if (ins && !form.insurance.includes(ins)) {
      set("insurance", [...form.insurance, ins]);
    }
    setInsInput("");
  };

  const removeIns = (ins) => set("insurance", form.insurance.filter(i => i !== ins));

  const valid = form.name.trim() && form.specialty && form.city && form.phone;

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    background: "#fff", border: "1px solid rgba(26,26,26,0.26)",
    borderRadius: 2, padding: "10px 12px", fontSize: 16,
    fontFamily: "'EB Garamond', serif", color: "#0A0A0A",
    outline: "none",
  };
  const labelStyle = { fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#555", marginBottom: 4, display: "block", fontWeight: 500 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <label style={labelStyle}>Full Name *</label>
        <input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="Dr. First Last" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={labelStyle}>Specialty *</label>
          <select style={inputStyle} value={form.specialty} onChange={e => set("specialty", e.target.value)}>
            <option value="">Select…</option>
            {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Clinic Name</label>
          <input style={inputStyle} value={form.clinic} onChange={e => set("clinic", e.target.value)} placeholder="Practice name" />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px", gap: 10 }}>
        <div>
          <label style={labelStyle}>City *</label>
          <input style={inputStyle} value={form.city} onChange={e => set("city", e.target.value)} placeholder="City" />
        </div>
        <div>
          <label style={labelStyle}>State</label>
          <input style={inputStyle} value={form.state} onChange={e => set("state", e.target.value)} placeholder="GA" maxLength={2} />
        </div>
        <div>
          <label style={labelStyle}>ZIP</label>
          <input style={inputStyle} value={form.zip} onChange={e => set("zip", e.target.value)} placeholder="30301" maxLength={5} />
        </div>
      </div>
      <div>
        <label style={labelStyle}>Phone *</label>
        <input style={inputStyle} value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="(404) 555-0000" type="tel" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={labelStyle}>Instagram Handle</label>
          <input style={inputStyle} value={form.instagram} onChange={e => set("instagram", e.target.value)} placeholder="handle (no @)" />
        </div>
        <div>
          <label style={labelStyle}>LinkedIn Slug</label>
          <input style={inputStyle} value={form.linkedin} onChange={e => set("linkedin", e.target.value)} placeholder="firstname-lastname-md" />
        </div>
      </div>
      <div>
        <label style={labelStyle}>Cookout Role 🔥</label>
        <select style={inputStyle} value={form.cookoutRole} onChange={e => set("cookoutRole", e.target.value)}>
          <option value="">Select a cookout role…</option>
          {COOKOUT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div>
        <label style={labelStyle}>Rating</label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Stars rating={form.rating} onChange={v => set("rating", v)} />
          <span style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 20, color: "#2B5C8A", lineHeight: 1 }}>{form.rating}.0</span>
          <GradeBadge rating={form.rating} />
        </div>
      </div>
      <div>
        <label style={labelStyle}>Insurance Networks</label>
        <div style={{ display: "flex", gap: 6 }}>
          <select style={{ ...inputStyle, flex: 1 }} value={insInput} onChange={e => setInsInput(e.target.value)}>
            <option value="">Add insurance…</option>
            {INSURANCE_OPTIONS.filter(i => !form.insurance.includes(i)).map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <button onClick={() => addIns(insInput)}
            style={{ background: "#2B5C8A", color: "#fff", border: "none", borderRadius: 2, padding: "0 14px", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12, whiteSpace: "nowrap" }}>
            Add
          </button>
        </div>
        {form.insurance.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {form.insurance.map(ins => (
              <span key={ins} style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(43,92,138,0.10)", border: "1px solid rgba(43,92,138,0.2)", borderRadius: 2, padding: "3px 8px", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#2B5C8A" }}>
                {ins}
                <button onClick={() => removeIns(ins)} style={{ background: "none", border: "none", cursor: "pointer", color: "#8B0000", fontSize: 12, lineHeight: 1, padding: 0 }}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div>
        <label style={labelStyle}>Bio</label>
        <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={form.bio} onChange={e => set("bio", e.target.value)} placeholder="A short bio…" />
      </div>
      <div style={{ display: "flex", gap: 16 }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#333" }}>
          <input type="checkbox" checked={form.telehealth} onChange={e => set("telehealth", e.target.checked)} />
          Telehealth
        </label>
        <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#333" }}>
          <input type="checkbox" checked={form.accepting} onChange={e => set("accepting", e.target.checked)} />
          Accepting New Patients
        </label>
      </div>
      <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
        <button onClick={onCancel}
          style={{ flex: 1, padding: "12px", background: "none", border: "1px solid rgba(26,26,26,0.26)", borderRadius: 2, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#444" }}>
          Cancel
        </button>
        <button onClick={() => valid && onSave(form)} disabled={!valid}
          style={{ flex: 2, padding: "12px", background: valid ? "#2B5C8A" : "#ccc", border: "none", borderRadius: 2, cursor: valid ? "pointer" : "not-allowed", fontFamily: "'DM Mono', monospace", fontSize: 13, color: "#fff", fontWeight: 500 }}>
          {initial ? "Save Changes" : "Invite to Cookout 🔥"}
        </button>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function DrCookout() {
  const [doctors, setDoctors] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [filterSpec, setFilterSpec] = useState("");
  const [filterIns, setFilterIns] = useState("");
  const [filterTele, setFilterTele] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [tab, setTab] = useState("invited"); // invited | search
  const [saving, setSaving] = useState(false);

  // Load on mount
  useEffect(() => {
    loadDoctors().then(data => {
      setDoctors(data || SEED_DOCTORS);
      setLoaded(true);
    });
  }, []);

  // Save on change
  useEffect(() => {
    if (!loaded) return;
    setSaving(true);
    saveDoctors(doctors).then(() => setTimeout(() => setSaving(false), 800));
  }, [doctors, loaded]);

  const invited = doctors.filter(d => d.invited);
  const uninvited = doctors.filter(d => !d.invited);

  const filterDocs = (list) => list.filter(d => {
    const q = search.toLowerCase();
    if (q && !d.name.toLowerCase().includes(q) && !d.specialty.toLowerCase().includes(q) && !d.city.toLowerCase().includes(q)) return false;
    if (filterSpec && d.specialty !== filterSpec) return false;
    if (filterIns && !d.insurance.includes(filterIns)) return false;
    if (filterTele && !d.telehealth) return false;
    return true;
  });

  const addDoctor = (form) => {
    const doc = { ...form, id: Date.now().toString(), invited: true };
    setDoctors(ds => [...ds, doc]);
    setShowAdd(false);
  };

  const updateDoctor = (form) => {
    setDoctors(ds => ds.map(d => d.id === form.id ? { ...d, ...form } : d));
    setEditDoc(null);
  };

  const uninviteDoc = (id) => {
    setDoctors(ds => ds.map(d => d.id === id ? { ...d, invited: false } : d));
  };

  const reinviteDoc = (id) => {
    setDoctors(ds => ds.map(d => d.id === id ? { ...d, invited: true } : d));
  };

  const deleteDoc = (id) => {
    setDoctors(ds => ds.filter(d => d.id !== id));
  };

  const displayList = tab === "invited" ? filterDocs(invited) : filterDocs(uninvited);

  const gradeCount = (g) => invited.filter(d => getGrade(d.rating) === g).length;

  return (
    <div style={{
      minHeight: "100vh",
      background: "#EFECE5",
      fontFamily: "'EB Garamond', serif",
    }}>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Cormorant+Garamond:wght@400;700&family=EB+Garamond:wght@400;500&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        ::placeholder { color: #999; font-family: 'EB Garamond', serif; }
        input, select, textarea { -webkit-appearance: none; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(43,92,138,0.3); border-radius: 2px; }
      `}</style>

      {/* Nav */}
      <div style={{
        background: "#0A0A0A", position: "sticky", top: 0, zIndex: 100,
        boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
      }}>
        <div style={{ maxWidth: 430, margin: "0 auto", padding: "0 16px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ color: "#CC5500", fontSize: 22 }}><GrillIcon /></div>
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 20, color: "#F7F5F0", lineHeight: 1 }}>
                DrCookout
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#4A80B5", letterSpacing: 1 }}>
                DOCTORS INVITED TO THE COOKOUT
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {saving && (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#4A80B5" }}>saving…</div>
            )}
            <button onClick={() => setShowAdd(true)}
              style={{ background: "#CC5500", border: "none", borderRadius: 2, color: "#fff", fontFamily: "'DM Mono', monospace", fontSize: 11, padding: "8px 14px", cursor: "pointer", fontWeight: 500, minHeight: 36 }}>
              + Invite
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 430, margin: "0 auto", padding: "0 12px" }}>

        {/* Stats Banner */}
        <div style={{ padding: "16px 0 8px", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
          {["A","B","C","D"].map(g => (
            <div key={g} style={{
              background: BUCKET_COLORS[g], borderRadius: 2, padding: "10px 12px",
              display: "flex", flexDirection: "column", alignItems: "center",
            }}>
              <div style={{ fontFamily: "'Bebas Neue', cursive", fontSize: 28, color: "#fff", lineHeight: 1 }}>{gradeCount(g)}</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>GRADE {g}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: "2px solid rgba(26,26,26,0.13)", marginTop: 8 }}>
          {[
            { key: "invited", label: `On The List (${invited.length})` },
            { key: "search", label: `Not Invited (${uninvited.length})` },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                flex: 1, padding: "10px 0", background: "none", border: "none",
                borderBottom: tab === t.key ? "2px solid #2B5C8A" : "2px solid transparent",
                marginBottom: -2, cursor: "pointer",
                fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 500,
                color: tab === t.key ? "#2B5C8A" : "#888",
                transition: "color 0.15s",
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div style={{ padding: "12px 0 8px", display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, specialty, city…"
            style={{ width: "100%", background: "#fff", border: "1px solid rgba(26,26,26,0.26)", borderRadius: 2, padding: "10px 14px", fontSize: 16, fontFamily: "'EB Garamond', serif", color: "#0A0A0A", outline: "none" }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <select value={filterSpec} onChange={e => setFilterSpec(e.target.value)}
              style={{ flex: 1, background: "#fff", border: "1px solid rgba(26,26,26,0.26)", borderRadius: 2, padding: "8px 10px", fontSize: 13, fontFamily: "'DM Mono', monospace", color: "#333", outline: "none" }}>
              <option value="">All Specialties</option>
              {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filterIns} onChange={e => setFilterIns(e.target.value)}
              style={{ flex: 1, background: "#fff", border: "1px solid rgba(26,26,26,0.26)", borderRadius: 2, padding: "8px 10px", fontSize: 13, fontFamily: "'DM Mono', monospace", color: "#333", outline: "none" }}>
              <option value="">All Insurance</option>
              {INSURANCE_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#555" }}>
            <input type="checkbox" checked={filterTele} onChange={e => setFilterTele(e.target.checked)} />
            Telehealth only
          </label>
        </div>

        {/* Results */}
        {!loaded ? (
          <div style={{ textAlign: "center", padding: 40, fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: "#888" }}>
            Firing up the grill…
          </div>
        ) : displayList.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🍖</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "#555", marginBottom: 8 }}>
              {tab === "invited" ? "No doctors on the list yet" : "Everyone's invited!"}
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#888" }}>
              {tab === "invited" ? "Use the + Invite button to add doctors to the cookout" : "No uninvited doctors match your search"}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingBottom: 80 }}>
            {displayList.map(doc => tab === "invited" ? (
              <DoctorCard key={doc.id} doc={doc} onEdit={setEditDoc} onUninvite={uninviteDoc} />
            ) : (
              <div key={doc.id} style={{
                background: "#F7F5F0", borderRadius: 3, borderTop: "3px solid #6B6B6B",
                boxShadow: "0 2px 8px rgba(10,10,10,0.06)", padding: 14,
                display: "flex", gap: 12, alignItems: "center",
              }}>
                <Avatar name={doc.name} size={44} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 16, color: "#0A0A0A" }}>{doc.name}</div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#666" }}>{doc.specialty} · {doc.city}, {doc.state}</div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => reinviteDoc(doc.id)}
                    style={{ background: "#2B5C8A", border: "none", borderRadius: 2, color: "#fff", fontFamily: "'DM Mono', monospace", fontSize: 10, padding: "6px 10px", cursor: "pointer" }}>
                    Invite 🔥
                  </button>
                  <button onClick={() => deleteDoc(doc.id)}
                    style={{ background: "rgba(139,0,0,0.08)", border: "1px solid rgba(139,0,0,0.2)", borderRadius: 2, color: "#8B0000", fontFamily: "'DM Mono', monospace", fontSize: 10, padding: "6px 10px", cursor: "pointer" }}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAdd && (
        <Modal title="Invite a Doctor to the Cookout 🔥" onClose={() => setShowAdd(false)}>
          <DoctorForm onSave={addDoctor} onCancel={() => setShowAdd(false)} />
        </Modal>
      )}
      {editDoc && (
        <Modal title="Edit Doctor Profile" onClose={() => setEditDoc(null)}>
          <DoctorForm initial={editDoc} onSave={updateDoctor} onCancel={() => setEditDoc(null)} />
        </Modal>
      )}

      {/* Bottom padding spacer */}
      <div style={{ height: 40 }} />
    </div>
  );
}
